<template>
  <b-card
    :title="header"
    body-class="mx-3 mx-md-5 mx-lg-5 mx-xl-5 px-3 px-md-5 px-lg-5 px-xl-5"
    class="mt-5 p-0 p-md-5 p-lg-5 p-xl-5 w-75"
    style="/**width: 550px**/"
  >
    <b-form onkeydown="return event.key != 'Enter'">
      <p class="pb-2 grey--text">{{ subheader }}</p>

      <b-form-group
        id="input-group-password"
        label="Password"
        label-for="input-password"
        v-if="currentStep === 1"
      >
        <b-form-input
          id="input-password"
          v-model="form.password"
          type="password"
          required
          placeholder="Enter password"
          :state="state.password"
          aria-describedby="input-password-feedback"
          @keyup.enter="nextStep"
        ></b-form-input>

        <b-form-invalid-feedback id="input-password-feedback">
          <!-- eslint-disable-next-line -->
          {{ errors.password }}
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Phone Number -->
      <b-form-group v-else-if="currentStep === 2">
        <label class="font-weight-bold">Phone Number</label>
        <p class="mb-2">
          Please select your country (e.g. +61) — the code is applied for you.
        </p>

        <vue-tel-input
          id="input-phone-number"
          ref="phoneInput"
          v-model="phonePrefill"
          default-country="AU"
          :preferred-countries="['AU']"
          :enabled-country-code="true"
          mode="international"
          :disabled-fetching-country="true"
          :wrapper-classes="{
            'is-invalid': state.phone_number === false,
            'is-valid': state.phone_number === true,
          }"
          @validate="onPhoneValidate"
          @input="onPhoneInput"
          @keydown.native="blockNonDigitKey"
          @paste.native="sanitizePhonePaste($event, this, 'phonePrefill')"
        ></vue-tel-input>

        <b-form-invalid-feedback
          v-if="!!errors.phone_number"
          :state="state.phone_number"
        >
          {{ errors.phone_number }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group id="input-group-code" v-else>
        <code-input
          :key="codeInputKey"
          :loading="false"
          @complete="onCompleteCode"
        />

        <div class="invalid-feedback d-block mt-2">{{ errors.code }}</div>

        <div class="mt-2">
          <b-button
            variant="link"
            class="p-0 resend-code-btn"
            :disabled="resending || loading || cooldown > 0"
            @click="onResendCode"
          >
            Resend code
            <span v-if="cooldown > 0">({{ cooldown }}s)</span>
          </b-button>
          <span v-if="resendNotice" class="text-success ml-2 small">{{
            resendNotice
          }}</span>
        </div>
      </b-form-group>

      <div class="justify-content-between">
        <b-container fluid>
          <b-row cols="2" class="verify-actions">
            <b-col cols="6" class="text-center text-lg-right text-xl-right">
              <b-button
                class="w-75 mr-0 mr-lg-2 mr-xl-2 btn-outlined bg-white text-dark"
                variant="outline-secondary"
                :to="{ name: 'user.settings-page', hash: '#account' }"
              >
                Cancel
              </b-button>
            </b-col>
            <b-col cols="6" class="text-center text-lg-left text-xl-left">
              <b-button
                variant="primary"
                class="w-75 ml-0 ml-lg-2 ml-xl-2"
                :disabled="loading"
                @click="nextStep"
              >
                <b-spinner v-if="loading" small label="Loading" />&nbsp;Continue
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { VueTelInput } from 'vue-tel-input'
import CodeInput from '@/components/inputs/CodeInput'
import {
  cleanPhoneDigits,
  blockNonDigitKey,
  sanitizePhonePaste,
  installPhoneDisplayWatch,
} from '@/utils/phonePolicy'

export default {
  components: {
    CodeInput,
    VueTelInput,
  },

  computed: {
    ...mapState({
      verificationToken: (state) => state.auth.verificationToken,
      user: (state) => state.auth.user,
    }),
  },

  data() {
    return {
      loading: false,
      resending: false,
      cooldown: 0,
      cooldownTimer: null,
      resendNotice: null,
      codeInputKey: 0,
      header: 'Verify Password',
      subheader: 'Enter your current password to change your mobile number',
      currentStep: 1,
      // vue-tel-input's v-model is the text shown/edited in the field (a
      // "+"-prefixed international string), NOT the same format as
      // form.phone_number, which stays in the combined format (country code
      // + number, no "+") the API expects - see onPhoneInput().
      phonePrefill: '',
      phoneValid: null,

      form: {
        password: null,
        phone_number: '',
        code: null,
      },
      state: {
        password: null,
        phone_number: null,
        code: null,
      },
      errors: {
        password: null,
        phone_number: null,
        code: null,
      },
    }
  },

  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    }
  },

  methods: {
    ...mapActions({
      getVerificationToken: 'auth/getVerificationToken',
      requestChangePhone: 'auth/requestChangePhone',
      verifyChangePhone: 'auth/verifyChangePhone',
    }),

    blockNonDigitKey,
    sanitizePhonePaste,

    startCooldown(seconds = 30) {
      this.cooldown = seconds
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
      }
      this.cooldownTimer = setInterval(() => {
        if (this.cooldown <= 1) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
          this.cooldown = 0
        } else {
          this.cooldown -= 1
        }
      }, 1000)
    },

    async onResendCode() {
      if (this.resending || this.loading || this.cooldown > 0) return

      this.resending = true
      this.resendNotice = null
      this.errors.code = null

      try {
        await this.requestChangePhone({
          phone_number: this.form.phone_number,
        })
        this.resendNotice = 'A new code has been sent.'
        this.codeInputKey += 1
        this.startCooldown()
      } catch (error) {
        const d = error.response && error.response.data
        const msg =
          (d && d.message) || 'Unable to resend code. Please try again shortly.'
        this.$bvToast.toast(msg, {
          title: 'Resend failed',
          variant: 'danger',
          autoHideDelay: 5000,
          solid: true,
        })
      } finally {
        this.resending = false
      }
    },

    // The library's `@validate` event only fires on mount and on true<->
    // false transitions of validity, so it's no use for live red/green
    // feedback (that's `onPhoneInput`, which fires every keystroke). It does
    // give `phoneValid` a correct answer if the user hits Continue without
    // ever touching the field.
    onPhoneValidate(payload) {
      this.phoneValid = payload.isValid
    },

    onPhoneInput(text, phoneObject) {
      this.phoneValid = phoneObject.isValid
      this.state.phone_number = phoneObject.isValid
      if (phoneObject.isValid) {
        this.errors.phone_number = null
        if (phoneObject.number && phoneObject.number.e164) {
          this.form.phone_number = cleanPhoneDigits(phoneObject.number.e164)
        }
      } else {
        this.form.phone_number = ''
        this.errors.phone_number = 'Enter a valid phone number.'
      }
    },

    nextStep() {
      if (this.currentStep === 1) {
        this.generateToken()
      }
      if (this.currentStep === 2) {
        this.changePhoneRequest()
      }
    },

    onCompleteCode(v) {
      this.form.code = v
      this.changePhoneVerify()
    },

    async generateToken() {
      this.loading = true
      await this.getVerificationToken({
        password: this.form.password,
      })
        .then(() => {
          this.currentStep++
          this.header = 'New Mobile Number'
          this.subheader = 'Enter new mobile number'
        })
        .catch((error) => {
          if (error.response.data) {
            this.state.password = false
            this.errors.password = error.response.data.message
          }
        })
      this.loading = false
    },

    async changePhoneRequest() {
      // Untouched field (phoneValid still null) or an invalid number: block
      // here rather than sending an empty/partial value to the API.
      if (!this.phoneValid || !this.form.phone_number) {
        this.state.phone_number = false
        this.errors.phone_number = 'Enter a valid phone number.'
        return
      }

      this.loading = true
      await this.requestChangePhone({ phone_number: this.form.phone_number })
        .then(() => {
          this.currentStep++
          this.header = 'Verify Mobile Number'
          this.subheader = `Input the code sent to: ${this.form.phone_number}`
          this.startCooldown()
        })
        .catch((error) => {
          if (error.response.data) {
            this.state.phone_number = false
            this.errors.phone_number = error.response.data.message
          }
        })
      this.loading = false
    },

    async changePhoneVerify() {
      this.loading = true
      await this.verifyChangePhone({
        token: this.form.code,
        phone_number: this.form.phone_number,
      })
        .then(() => {
          this.$router.push({
            name: 'user.settings-page',
            hash: '#account',
          })
        })
        .catch((error) => {
          const d = error.response && error.response.data
          const msg = d
            ? d.message ||
              (d.errors && Object.values(d.errors).flat()[0]) ||
              'Invalid or expired verification code.'
            : error.message || 'Invalid or expired verification code.'
          if (d) {
            this.form.code = null
            this.errors.code = d.message || msg
          }
          this.$bvToast.toast(msg, {
            title: 'Verification failed',
            variant: 'danger',
            autoHideDelay: 5000,
            solid: true,
          })
        })
      this.loading = false
    },
  },

  watch: {
    'form.password'() {
      this.state.password = this.form.password.length !== 0 ? null : false
      this.errors.password = 'This field is required'
    },

    // form.phone_number's state/errors are driven by onPhoneInput() (the
    // vue-tel-input's own @input event), not a watcher here - it knows the
    // actual validity, not just a digit count.

    // The phone field only exists in the DOM once currentStep reaches 2
    // (v-else-if="currentStep === 2"), so its ref isn't available until
    // then - install the display-cleanup watch once it actually mounts,
    // whether that's immediately (user.has_password is false, so created()
    // starts at step 2 directly) or after nextStep() advances to it.
    currentStep(val) {
      if (val === 2) {
        this.$nextTick(() => installPhoneDisplayWatch(this, 'phoneInput'))
      }
    },
  },

  created() {
    this.header = this.user.has_password
      ? 'Verify Password'
      : 'New Mobile Number'
    this.subheader = this.user.has_password
      ? 'Enter your current password to change your mobile number'
      : 'Enter new mobile number'
    this.currentStep = this.user.has_password ? 1 : 2
  },
}
</script>

<style lang="scss" scoped>
.btn.btn-outlined {
  padding: 4px 32px;
}

.btn {
  height: 36px;
}

// vue-tel-input ships its own box styling (thin 1px border, 3px radius) on
// its wrapper, with the inner <input> deliberately borderless - so it's the
// wrapper that needs to match .form-control's box, not the input. Same
// styling as ProfileForm / the onboarding pages so the picker looks
// identical everywhere.
/deep/ .vue-tel-input {
  height: 50px;
  border: 2px solid #e5e5e5;
  border-radius: 8px;

  &:focus-within {
    border-color: #2c66d5;
  }

  &.is-invalid {
    border-color: #dc3545;
  }

  &.is-valid {
    border-color: #28a745;
  }
}

/deep/ .vti__dropdown {
  border-radius: 6px 0 0 6px;
}

// .vti__selection is already display:flex + align-items:center (library
// default), which centers each child's box correctly - but a global
// line-height inherited onto its text spans (+61, the dropdown arrow)
// inflates their box well beyond the glyph itself, so the box centers
// correctly while the text still reads as offset inside it.
/deep/ .vti__selection {
  line-height: 1;
}

/deep/ .vti__input {
  height: 100%;
  // Otherwise inherits the global body line-height (~1.5), which sits the
  // typed number's glyph lower in its box than the +61/arrow spans right
  // next to it (fixed to line-height:1 above) - same cause, other side.
  line-height: 1;
  border: none;
  border-radius: 0 6px 6px 0;

  &::placeholder {
    color: #b2b2b2;
  }
}

/deep/ .vti__dropdown-list {
  border-radius: 8px;
  border-color: #e5e5e5;
  margin-top: 4px;
}

/* Large screens: the buttons sit as a natural-size pair on the left, aligned
   with the rest of the content. Each column keeps the old button footprint
   (75% of a half-width column = 37.5% of the row). */
@media (min-width: 992px) {
  .verify-actions {
    justify-content: flex-start;
  }

  .verify-actions > [class*='col'] {
    flex: 0 0 auto;
    width: 37.5%;
    max-width: none;
  }

  .verify-actions .btn {
    width: 100% !important;
  }
}

/* Small screens (< lg): centre the heading + description, let the card use the
   full width, trim its heavy body padding, and show the buttons side-by-side
   filling their half-columns with a clear gap, down to 320px. */
@media (max-width: 991.98px) {
  .card.w-75 {
    width: 100% !important;
    margin-top: 2rem !important;
  }

  .card /deep/ .card-body {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 1.25rem !important;
  }

  .card /deep/ .card-title,
  .card .grey--text {
    text-align: center;
  }

  .verify-actions .btn {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .verify-actions > [class*='col']:first-child {
    padding-right: 0.5rem !important;
  }

  .verify-actions > [class*='col']:last-child {
    padding-left: 0.5rem !important;
  }
}
</style>
